import React, { useEffect } from 'react';
import { Question } from '../types';
import { images } from '../services/api';
import { imageCache } from '../utils/imageCache';

interface ImagePreloaderProps {
  questions: Question[];
}

/**
 * Component that preloads images for upcoming questions
 * This component doesn't render anything visible
 */
const ImagePreloader: React.FC<ImagePreloaderProps> = ({ questions }) => {
  useEffect(() => {
    // Function to preload an image URL
    const preloadImage = (url: string) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Resolve even on error to continue preloading others
        img.src = url;
      });
    };

    // Function to get a signed URL for an image key
    const getSignedUrl = async (imageUrl: string): Promise<string | null> => {
      try {
        // Process the image key
        let key = imageUrl;
        if (key.startsWith('http')) {
          const url = new URL(key);
          const pathParts = url.pathname.split('/');
          key = pathParts[pathParts.length - 1];
        } else if (key.includes('/')) {
          const pathParts = key.split('/');
          key = pathParts[pathParts.length - 1];
        }

        // Check if we already have this URL in cache
        const cachedUrl = imageCache.get(key);
        if (cachedUrl) {
          console.log('[ImagePreloader] Using cached URL for:', key);
          return cachedUrl;
        }

        // Fetch the signed URL from the backend
        console.log('[ImagePreloader] Fetching signed URL for:', key);
        const url = await images.getSignedUrl(key);
        
        // Store in cache
        imageCache.set(key, url);
        
        return url;
      } catch (err) {
        console.error('[ImagePreloader] Error fetching signed URL:', err);
        return null;
      }
    };

    // Main function to preload images for questions
    const preloadImagesForQuestions = async () => {
      // Only preload for up to 3 upcoming questions to avoid too many requests
      const questionsToPreload = questions.slice(0, 3);
      
      for (const question of questionsToPreload) {
        // Preload the main visual component if it exists
        if (question.visual_component_url) {
          const signedUrl = await getSignedUrl(question.visual_component_url);
          if (signedUrl) {
            console.log('[ImagePreloader] Preloading image:', signedUrl);
            await preloadImage(signedUrl);
          }
        }
        
        // Preload the image_url if it exists
        if (question.image_url) {
          const signedUrl = await getSignedUrl(question.image_url);
          if (signedUrl) {
            console.log('[ImagePreloader] Preloading image_url:', signedUrl);
            await preloadImage(signedUrl);
          }
        }
      }
    };

    // Start preloading if there are questions
    if (questions.length > 0) {
      preloadImagesForQuestions();
    }
  }, [questions]);

  // This component doesn't render anything visible
  return null;
};

export default ImagePreloader;
